import React, { memo, useCallback, useEffect, useRef, useState, } from 'react';
import Animated, { runOnJS, withTiming, useSharedValue, useDerivedValue, useAnimatedStyle, } from 'react-native-reanimated';
import LinearGradient from 'utils/packages/linearGradient';
import Video from 'utils/packages/rnVideo';
import { StyleSheet, TouchableOpacity, TouchableWithoutFeedback, View, } from 'react-native';
import Text from 'design-system/Text';
import { IconClose, Indicator, Spacer, hitSlop, rem } from 'design-system';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { useMainStackNavigation } from 'utils/types/navigationV6';
const gradientColors = ['rgb(0, 0, 0)', 'rgba(0, 0, 0, 0)'];
const LearnVideo = ({ index, title, videoUrl, isFocused, handleNext, handlePrev, isGestureActive, }) => {
    const focusedRef = useRef(isFocused);
    const paused = useSharedValue(true);
    const videoPlayerRef = useRef(null);
    const progressSharedValue = useSharedValue(0);
    const navigation = useMainStackNavigation();
    const colors = useColors();
    const styles = useStyles(styleSet);
    const [loading, setLoading] = useState(true);
    const reset = useCallback(() => {
        videoPlayerRef.current?.seek(0);
        progressSharedValue.value = 0;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (!isFocused && focusedRef.current) {
            reset();
        }
        if (isFocused) {
            paused.value = false;
        }
        focusedRef.current = isFocused;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFocused, reset]);
    const onCancel = useCallback(() => {
        navigation.pop();
    }, []);
    const timeoutRef = useRef(undefined);
    const handleOnEnd = useCallback(() => {
        paused.value = true;
        timeoutRef.current = setTimeout(() => {
            handleNext();
        }, 200);
        return () => {
            clearTimeout(timeoutRef.current);
        };
    }, [handleNext, paused]);
    const handlePressPrev = useCallback(() => {
        paused.value = true;
        handlePrev();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handlePrev]);
    const handlePressNext = useCallback(() => {
        paused.value = true;
        handleNext();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleNext]);
    const handleReady = useCallback(() => {
        setLoading(false);
    }, []);
    const handleProgress = useCallback((progressObj) => {
        const progress = progressObj.currentTime / progressObj.seekableDuration;
        if (progress > 0 && isFocused) {
            progressSharedValue.value = withTiming(progress);
        }
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isFocused]);
    const progressBarStyles = useAnimatedStyle(() => ({
        width: `${progressSharedValue.value * 100}%`,
    }), []);
    const pauseVideo = useCallback(() => {
        videoPlayerRef.current?.pause();
    }, []);
    const resumeVideo = useCallback(() => {
        videoPlayerRef.current?.resume();
    }, []);
    useDerivedValue(() => {
        const shouldPause = isGestureActive.value || paused.value || !isFocused;
        if (shouldPause) {
            runOnJS(pauseVideo)();
        }
        else {
            runOnJS(resumeVideo)();
        }
    }, [isFocused, isGestureActive, pauseVideo, paused, resumeVideo]);
    return (<View style={styles.container}>
      <Video paused volume={1.0} muted={false} playWhenInactive onEnd={handleOnEnd} ref={videoPlayerRef} onProgress={handleProgress} onReadyForDisplay={handleReady} source={{
            type: 'm3u8',
            uri: videoUrl,
        }} resizeMode="cover" useTextureView={false} ignoreSilentSwitch="ignore" style={staticStyles.video}/>
      {loading ? (<View style={styles.spinnerContainer} pointerEvents="none">
          <Indicator white/>
        </View>) : null}
      <View style={styles.buttonContainer}>
        <TouchableWithoutFeedback onPress={handlePressPrev} disabled={!isFocused || index === 0}>
          <View style={styles.controlBtn}/>
        </TouchableWithoutFeedback>
        <View style={styles.flex}/>
        <TouchableWithoutFeedback onPress={handlePressNext} disabled={!isFocused}>
          <View style={styles.controlBtn}/>
        </TouchableWithoutFeedback>
      </View>
      <View style={styles.videoInfo}>
        <LinearGradient colors={gradientColors} style={styles.linearGradient}>
          <View style={styles.progressBar}>
            <Animated.View style={[styles.progressBarStyles, progressBarStyles]}/>
          </View>
          <Spacer.H16 />
          <View style={styles.row}>
            <Text Text-16 White flex numberOfLines={1}>
              {title}
            </Text>
            <Spacer.W16 />
            <TouchableOpacity hitSlop={hitSlop} onPress={onCancel}>
              <IconClose alt color={colors.text.white}/>
            </TouchableOpacity>
          </View>
        </LinearGradient>
      </View>
    </View>);
};
const staticStyles = StyleSheet.create({
    video: {
        flex: 1,
    },
});
const styleSet = createStyleSheets((colors) => ({
    container: {
        flex: 1,
        overflow: 'hidden',
        borderRadius: rem(16),
    },
    videoInfo: {
        top: 0,
        left: 0,
        right: 0,
        height: rem(74),
        position: 'absolute',
    },
    progressBar: {
        height: 2,
        borderRadius: rem(2),
        backgroundColor: colors.elements.black,
    },
    row: {
        flexDirection: 'row',
    },
    spinnerContainer: {
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        alignItems: 'center',
        position: 'absolute',
        justifyContent: 'center',
    },
    linearGradient: {
        flex: 1,
        paddingTop: rem(16),
        paddingHorizontal: rem(16),
    },
    progressBarStyles: {
        flex: 1,
        backgroundColor: colors.elements.white,
    },
    buttonContainer: {
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        position: 'absolute',
        flexDirection: 'row',
    },
    controlBtn: {
        flex: 1,
    },
    flex: {
        flex: 1,
    },
}));
export default memo(LearnVideo);
